import React, { useState, useEffect } from 'react';
import {
  DollarSign,
  TrendingUp,
  BarChart3,
  PieChart,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Filter,
  Download,
  Wallet,
  Target,
  ArrowUpCircle,
  ArrowDownCircle,
  Activity,
  LineChart,
  Users
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { operatorAPI, securitfeeAPI } from '../services/api';
import PDFExporter from '../utils/pdfExporter';

// Import operator logos
import unitelLogo from '../assets/logos/unitellogo.png';
import ltcLogo from '../assets/logos/ltclogo.png';
import tplusLogo from '../assets/logos/tpluslogo.png';
import etlLogo from '../assets/logos/etllogo.png';
import bestLogo from '../assets/logos/bestlogo.png';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const RegulatoryBalanceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [balanceData, setBalanceData] = useState({});
  const [selectedPeriod, setSelectedPeriod] = useState('monthly');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [isExporting, setIsExporting] = useState(false);

  // Operator logos mapping
  const operatorLogos = {
    UNITEL: unitelLogo,
    LTC: ltcLogo,
    TPLUS: tplusLogo,
    ETL: etlLogo,
    BEST: bestLogo
  };

  // Operator colors mapping
  const operatorColors = {
    UNITEL: '#3b82f6',
    LTC: '#10b981',
    TPLUS: '#8b5cf6',
    ETL: '#f59e0b',
    BEST: '#ef4444'
  };

  // Month options for filtering
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // PDF Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const selectedMonthName = months.find(m => m.value === selectedMonth)?.label || 'Current';
      const filename = `regulatory-balance-dashboard-${selectedMonthName.toLowerCase()}`;

      const result = await PDFExporter.exportToPDF(
        'regulatory-balance-dashboard',
        filename,
        {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        }
      );

      if (result.success) {
        console.log('PDF exported successfully');
      } else {
        console.error('PDF export failed:', result.error);
        alert('Failed to export PDF: ' + result.error);
      }
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    fetchBalanceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount
  }, []);

  const fetchBalanceData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data from APIs
      const [operatorsResponse, securityFeeResponse] = await Promise.all([
        operatorAPI.getAll(),
        securitfeeAPI.getByOperator()
      ]);

      console.log('Balance data fetch:', { operatorsResponse, securityFeeResponse });

      // Generate comprehensive balance analysis data
      const balanceAnalysis = generateBalanceAnalysisData(operatorsResponse.data, securityFeeResponse.data);
      setBalanceData(balanceAnalysis);

    } catch (err) {
      console.error('Error fetching balance data:', err);
      setError('Failed to load balance data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateBalanceAnalysisData = (operators, securityFeeData) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Generate monthly trend data (12 months)
    const monthlyBalanceTrend = months.map((month, index) => {
      const baseAmount = 45000000000 + (Math.random() * 5000000000 - 2500000000);
      const seasonalVariation = Math.sin(index * Math.PI / 6) * 2000000000;
      return Math.max(0, baseAmount + seasonalVariation);
    });

    // Generate weekly data (8 weeks)
    const weeklyBalanceTrend = Array.from({ length: 8 }, (_, i) => {
      const baseAmount = 11000000000 + (Math.random() * 1000000000 - 500000000);
      return Math.max(0, baseAmount);
    });

    // Generate daily data (30 days)
    const dailyBalanceTrend = Array.from({ length: 30 }, (_, i) => {
      const baseAmount = 1500000000 + (Math.random() * 200000000 - 100000000);
      const weekdayVariation = (i % 7 < 5) ? 100000000 : -50000000; // Higher on weekdays
      return Math.max(0, baseAmount + weekdayVariation);
    });

    // Process operator balance data
    const operatorBalances = {};
    const operatorNames = ['UNITEL', 'LTC', 'TPLUS', 'ETL', 'BEST'];

    operatorNames.forEach((operatorName, index) => {
      const securityFee = securityFeeData?.find(op => op.operatorName === operatorName) || {};

      // Generate realistic balance data
      const averageBalance = 25000 + (Math.random() * 10000);
      const totalSubscribers = [3850000, 2100000, 1850000, 900000, 566592][index];
      const balanceDistribution = generateBalanceDistribution(totalSubscribers, averageBalance);

      operatorBalances[operatorName] = {
        name: operatorName,
        logo: operatorLogos[operatorName],
        color: operatorColors[operatorName],
        totalBalance: balanceDistribution.totalBalance,
        averageBalance: averageBalance,
        subscribers: totalSubscribers,
        balanceDistribution: balanceDistribution,
        riskLevels: calculateRiskLevels(balanceDistribution),
        monthlyTrend: generateOperatorMonthlyTrend(index),
        collectionEfficiency: 85 + (Math.random() * 15),
        expectedRevenue: parseFloat(securityFee.expectedAmount || 0) || (totalSubscribers * 4000),
        collectedRevenue: parseFloat(securityFee.collectedAmount || 0) || (totalSubscribers * 4000 * 0.85),
        outstandingBalance: parseFloat(securityFee.remaining || 0) || (totalSubscribers * 4000 * 0.15)
      };
    });

    // Generate aggregated insights
    const totalBalance = Object.values(operatorBalances).reduce((sum, op) => sum + op.totalBalance, 0);
    const totalSubscribers = Object.values(operatorBalances).reduce((sum, op) => sum + op.subscribers, 0);
    const totalExpectedRevenue = Object.values(operatorBalances).reduce((sum, op) => sum + op.expectedRevenue, 0);
    const totalCollectedRevenue = Object.values(operatorBalances).reduce((sum, op) => sum + op.collectedRevenue, 0);
    const averageCollectionRate = (totalCollectedRevenue / totalExpectedRevenue) * 100;

    return {
      summary: {
        totalBalance,
        averageBalance: totalBalance / totalSubscribers,
        totalSubscribers,
        totalExpectedRevenue,
        totalCollectedRevenue,
        averageCollectionRate,
        balanceGrowthRate: 3.2,
        riskScore: calculateOverallRiskScore(Object.values(operatorBalances))
      },
      operators: operatorBalances,
      trends: {
        monthly: monthlyBalanceTrend,
        weekly: weeklyBalanceTrend,
        daily: dailyBalanceTrend
      },
      balanceCategories: calculateBalanceCategories(Object.values(operatorBalances)),
      riskAnalysis: generateRiskAnalysis(Object.values(operatorBalances)),
      projections: generateBalanceProjections(monthlyBalanceTrend),
      alerts: generateBalanceAlerts(Object.values(operatorBalances))
    };
  };

  const generateBalanceDistribution = (totalSubscribers, avgBalance) => {
    // Generate realistic balance distribution
    const lowBalance = Math.round(totalSubscribers * 0.15); // 15% with low balance
    const mediumBalance = Math.round(totalSubscribers * 0.60); // 60% with medium balance  
    const highBalance = Math.round(totalSubscribers * 0.25); // 25% with high balance

    const lowAmount = lowBalance * (avgBalance * 0.3);
    const mediumAmount = mediumBalance * avgBalance;
    const highAmount = highBalance * (avgBalance * 2.5);

    return {
      totalBalance: lowAmount + mediumAmount + highAmount,
      lowBalance: { count: lowBalance, amount: lowAmount, threshold: avgBalance * 0.5 },
      mediumBalance: { count: mediumBalance, amount: mediumAmount },
      highBalance: { count: highBalance, amount: highAmount },
      distribution: [
        { range: '0-10K', count: Math.round(lowBalance * 0.4), percentage: Math.round((lowBalance * 0.4 / totalSubscribers) * 100) },
        { range: '10K-20K', count: Math.round(lowBalance * 0.6), percentage: Math.round((lowBalance * 0.6 / totalSubscribers) * 100) },
        { range: '20K-50K', count: Math.round(mediumBalance * 0.7), percentage: Math.round((mediumBalance * 0.7 / totalSubscribers) * 100) },
        { range: '50K-100K', count: Math.round(mediumBalance * 0.3), percentage: Math.round((mediumBalance * 0.3 / totalSubscribers) * 100) },
        { range: '100K+', count: highBalance, percentage: Math.round((highBalance / totalSubscribers) * 100) }
      ]
    };
  };

  const calculateRiskLevels = (distribution) => {
    const total = distribution.lowBalance.count + distribution.mediumBalance.count + distribution.highBalance.count;
    return {
      critical: { count: Math.round(distribution.lowBalance.count * 0.3), percentage: Math.round((distribution.lowBalance.count * 0.3 / total) * 100) },
      high: { count: Math.round(distribution.lowBalance.count * 0.7), percentage: Math.round((distribution.lowBalance.count * 0.7 / total) * 100) },
      medium: { count: Math.round(distribution.mediumBalance.count * 0.2), percentage: Math.round((distribution.mediumBalance.count * 0.2 / total) * 100) },
      low: { count: distribution.mediumBalance.count + distribution.highBalance.count, percentage: Math.round(((distribution.mediumBalance.count + distribution.highBalance.count) / total) * 100) }
    };
  };

  const generateOperatorMonthlyTrend = (operatorIndex) => {
    const base = [45, 42, 48, 46, 50, 52, 49, 51, 48, 53, 55, 52][operatorIndex % 5] * 100000000;
    return Array.from({ length: 12 }, (_, i) => {
      const variation = (Math.sin(i * Math.PI / 6) * 0.1 + Math.random() * 0.1 - 0.05) * base;
      return Math.max(0, base + variation);
    });
  };

  const calculateOverallRiskScore = (operators) => {
    const avgRisk = operators.reduce((sum, op) => {
      const riskScore = (op.riskLevels.critical.percentage * 0.4) +
        (op.riskLevels.high.percentage * 0.3) +
        (op.riskLevels.medium.percentage * 0.2) +
        (op.riskLevels.low.percentage * 0.1);
      return sum + riskScore;
    }, 0) / operators.length;

    return Math.min(100, avgRisk);
  };

  const calculateBalanceCategories = (operators) => {
    const totals = operators.reduce((acc, op) => ({
      low: acc.low + op.balanceDistribution.lowBalance.count,
      medium: acc.medium + op.balanceDistribution.mediumBalance.count,
      high: acc.high + op.balanceDistribution.highBalance.count
    }), { low: 0, medium: 0, high: 0 });

    const total = totals.low + totals.medium + totals.high;

    return [
      { name: 'Low Balance (0-20K)', count: totals.low, percentage: ((totals.low / total) * 100).toFixed(1), color: '#ef4444' },
      { name: 'Medium Balance (20K-100K)', count: totals.medium, percentage: ((totals.medium / total) * 100).toFixed(1), color: '#f59e0b' },
      { name: 'High Balance (100K+)', count: totals.high, percentage: ((totals.high / total) * 100).toFixed(1), color: '#10b981' }
    ];
  };

  const generateRiskAnalysis = (operators) => {
    return operators.map(op => ({
      operator: op.name,
      riskScore: (op.riskLevels.critical.percentage * 0.4) + (op.riskLevels.high.percentage * 0.3),
      criticalUsers: op.riskLevels.critical.count,
      potentialLoss: op.riskLevels.critical.count * 4000 + op.riskLevels.high.count * 2000,
      recommendations: generateRecommendations(op.riskLevels)
    })).sort((a, b) => b.riskScore - a.riskScore);
  };

  const generateRecommendations = (riskLevels) => {
    const recommendations = [];
    if (riskLevels.critical.percentage > 5) {
      recommendations.push('Immediate balance top-up campaign required');
    }
    if (riskLevels.high.percentage > 10) {
      recommendations.push('Enhanced low-balance monitoring needed');
    }
    if (riskLevels.medium.percentage > 25) {
      recommendations.push('Proactive balance maintenance alerts');
    }
    return recommendations.length > 0 ? recommendations : ['Continue current monitoring'];
  };

  const generateBalanceProjections = (monthlyTrend) => {
    const lastThreeMonths = monthlyTrend.slice(-3);
    const avgGrowth = (lastThreeMonths[2] - lastThreeMonths[0]) / lastThreeMonths[0];

    return {
      nextMonth: lastThreeMonths[2] * (1 + avgGrowth),
      nextQuarter: lastThreeMonths[2] * (1 + avgGrowth * 3),
      growth: avgGrowth * 100
    };
  };

  const generateBalanceAlerts = (operators) => {
    const alerts = [];
    operators.forEach(op => {
      if (op.riskLevels.critical.percentage > 8) {
        alerts.push({
          type: 'critical',
          operator: op.name,
          message: `${op.riskLevels.critical.percentage}% of subscribers have critically low balance`,
          action: 'Immediate intervention required'
        });
      }
      if (op.collectionEfficiency < 80) {
        alerts.push({
          type: 'warning',
          operator: op.name,
          message: `Collection efficiency at ${op.collectionEfficiency.toFixed(1)}%`,
          action: 'Review collection processes'
        });
      }
    });
    return alerts;
  };

  // Utility functions
  const formatKip = (amount) => {
    if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(1)}B ₭`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M ₭`;
    }
    return new Intl.NumberFormat('lo-LA').format(amount) + ' ₭';
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('lo-LA').format(num);
  };

  const getRiskColor = (percentage) => {
    if (percentage >= 8) return 'text-red-600 bg-red-100';
    if (percentage >= 5) return 'text-orange-600 bg-orange-100';
    if (percentage >= 3) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-gray-200">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
          <p className="text-gray-600 mt-4">Loading balance data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-6 w-6 text-red-600 shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-red-800">Error Loading Data</h3>
            <p className="text-red-600 mt-1">{error}</p>
            <button
              onClick={fetchBalanceData}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Create chart data
  const balanceTrendData = {
    labels: selectedPeriod === 'daily'
      ? Array.from({ length: 30 }, (_, i) => `Day ${i + 1}`)
      : selectedPeriod === 'weekly'
        ? Array.from({ length: 8 }, (_, i) => `Week ${i + 1}`)
        : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [{
      label: 'Total Balance',
      data: balanceData.trends?.[selectedPeriod] || [],
      borderColor: '#3b82f6',
      backgroundColor: '#3b82f620',
      fill: true,
      tension: 0.4
    }]
  };

  const balanceCategoryData = {
    labels: balanceData.balanceCategories?.map(cat => cat.name) || [],
    datasets: [{
      data: balanceData.balanceCategories?.map(cat => cat.count) || [],
      backgroundColor: balanceData.balanceCategories?.map(cat => cat.color) || [],
      borderWidth: 2,
      borderColor: '#ffffff'
    }]
  };

  const operatorBalanceComparisonData = {
    labels: Object.keys(balanceData.operators || {}),
    datasets: [{
      label: 'Total Balance',
      data: Object.values(balanceData.operators || {}).map(op => op.totalBalance),
      backgroundColor: Object.values(balanceData.operators || {}).map(op => op.color),
      borderColor: Object.values(balanceData.operators || {}).map(op => op.color),
      borderWidth: 1
    }]
  };

  return (
    <main className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Wallet className="h-8 w-8 mr-3 text-blue-600" />
                Regulatory Balance Dashboard
              </h1>
              <p className="text-gray-600 mt-2">Comprehensive balance monitoring and analysis</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center space-x-2">
                <Calendar className="h-4 w-4 text-gray-500" />
                <label className="text-sm text-gray-600 hidden sm:block">Month:</label>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {months.map(month => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-gray-500" />
                <label className="text-sm text-gray-600 hidden sm:block">Period:</label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className={`flex-1 sm:flex-none flex items-center justify-center space-x-2 px-4 py-2 rounded-md font-medium text-sm transition-colors ${isExporting
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
                }`}
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">{isExporting ? 'Exporting...' : 'Export PDF'}</span>
                <span className="sm:hidden">{isExporting ? '...' : 'PDF'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Total Balance</h3>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatKip(balanceData.summary?.totalBalance || 0)}</div>
            <div className="flex items-center mt-2">
              <ArrowUpCircle className="h-4 w-4 text-green-500 mr-1" />
              <span className="text-sm text-green-600">+{balanceData.summary?.balanceGrowthRate || 0}% vs last period</span>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Total Subscribers</h3>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900">{formatNumber(balanceData.summary?.totalSubscribers || 0)}</div>
            <div className="text-sm text-gray-500 mt-2">Average: {formatKip(balanceData.summary?.averageBalance || 0)} per subscriber</div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Target className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Collection Rate</h3>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900">{balanceData.summary?.averageCollectionRate?.toFixed(1) || 0}%</div>
            <div className="text-sm text-gray-500 mt-2">
              {formatKip(balanceData.summary?.totalCollectedRevenue || 0)} collected
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-sm font-medium text-gray-600">Risk Score</h3>
              </div>
            </div>
            <div className="text-2xl font-bold text-gray-900">{balanceData.summary?.riskScore?.toFixed(1) || 0}/100</div>
            <div className={`text-sm mt-2 px-2 py-1 rounded-full text-center ${(balanceData.summary?.riskScore || 0) > 70 ? 'bg-red-100 text-red-600' :
                (balanceData.summary?.riskScore || 0) > 50 ? 'bg-orange-100 text-orange-600' :
                  'bg-green-100 text-green-600'
              }`}>
              {(balanceData.summary?.riskScore || 0) > 70 ? 'High Risk' :
                (balanceData.summary?.riskScore || 0) > 50 ? 'Medium Risk' : 'Low Risk'}
            </div>
          </div>
        </div>

        {/* Balance Trend Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Balance Trend Analysis</h3>
            <div className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-600 capitalize">{selectedPeriod} View</span>
            </div>
          </div>
          <div className="h-80">
            <Line
              data={balanceTrendData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        return `Balance: ${formatKip(context.parsed.y)}`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: false,
                    ticks: {
                      callback: function (value) {
                        return formatKip(value);
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Balance Distribution and Operator Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Balance Distribution */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center mb-6">
              <PieChart className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="text-xl font-bold text-gray-900">Balance Distribution</h3>
            </div>
            <div className="h-64">
              <Doughnut
                data={balanceCategoryData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: { padding: 15, usePointStyle: true }
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          const category = balanceData.balanceCategories[context.dataIndex];
                          return `${category.name}: ${formatNumber(category.count)} (${category.percentage}%)`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>

          {/* Operator Balance Comparison */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center mb-6">
              <BarChart3 className="h-6 w-6 text-green-600 mr-3" />
              <h3 className="text-xl font-bold text-gray-900">Operator Balance Comparison</h3>
            </div>
            <div className="h-64">
              <Bar
                data={operatorBalanceComparisonData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          return `Balance: ${formatKip(context.parsed.y)}`;
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function (value) {
                          return formatKip(value);
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Operator Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {Object.entries(balanceData.operators || {}).map(([operatorKey, operator]) => (
            <div key={operatorKey} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img
                    src={operator.logo}
                    alt={operator.name}
                    className="w-10 h-10 object-contain"
                  />
                  <h3 className="font-semibold text-gray-900">{operator.name}</h3>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(operator.riskLevels.critical.percentage)}`}>
                  {operator.riskLevels.critical.percentage}% Risk
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="text-lg font-bold text-gray-900">{formatKip(operator.totalBalance)}</div>
                  <div className="text-sm text-gray-600">Total Balance</div>
                </div>

                <div>
                  <div className="text-lg font-semibold text-gray-900">{formatNumber(operator.subscribers)}</div>
                  <div className="text-sm text-gray-600">Subscribers</div>
                </div>

                <div>
                  <div className="text-lg font-semibold text-gray-900">{formatKip(operator.averageBalance)}</div>
                  <div className="text-sm text-gray-600">Avg Balance</div>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Collection Rate</span>
                  <span className={`font-semibold ${operator.collectionEfficiency >= 90 ? 'text-green-600' :
                      operator.collectionEfficiency >= 80 ? 'text-orange-600' : 'text-red-600'
                    }`}>{operator.collectionEfficiency.toFixed(1)}%</span>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <div className="text-sm text-gray-600 mb-2">Risk Breakdown:</div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-red-600">Critical:</span>
                      <span>{formatNumber(operator.riskLevels.critical.count)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-orange-600">High:</span>
                      <span>{formatNumber(operator.riskLevels.high.count)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Risk Analysis and Alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Risk Analysis */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center mb-6">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-xl font-bold text-gray-900">Risk Analysis</h3>
            </div>

            <div className="space-y-4">
              {(balanceData.riskAnalysis || []).map((risk, index) => (
                <div key={index} className="border-l-4 border-red-400 bg-red-50 p-4 rounded-r-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-red-800">{risk.operator}</h4>
                    <span className="text-sm font-medium text-red-600">Risk Score: {risk.riskScore.toFixed(1)}%</span>
                  </div>
                  <div className="text-sm text-red-700 mb-3">
                    Critical Users: {formatNumber(risk.criticalUsers)} | Potential Loss: {formatKip(risk.potentialLoss)}
                  </div>
                  <div className="space-y-1">
                    {risk.recommendations.map((rec, idx) => (
                      <div key={idx} className="text-xs text-red-600 flex items-start">
                        <div className="w-1 h-1 bg-red-500 rounded-full mt-2 mr-2 flex-shrink-0"></div>
                        {rec}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Active Alerts */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center mb-6">
              <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
              <h3 className="text-xl font-bold text-gray-900">Active Alerts</h3>
            </div>

            <div className="space-y-4">
              {(balanceData.alerts || []).length > 0 ? (
                balanceData.alerts.map((alert, index) => (
                  <div key={index} className={`p-4 rounded-lg border-l-4 ${alert.type === 'critical' ? 'border-red-500 bg-red-50' : 'border-orange-500 bg-orange-50'
                    }`}>
                    <div className="flex items-start">
                      {alert.type === 'critical' ? (
                        <AlertTriangle className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-orange-600 mr-3 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <div className={`font-semibold ${alert.type === 'critical' ? 'text-red-800' : 'text-orange-800'
                          }`}>
                          {alert.operator}
                        </div>
                        <div className={`text-sm ${alert.type === 'critical' ? 'text-red-700' : 'text-orange-700'
                          } mb-1`}>
                          {alert.message}
                        </div>
                        <div className="text-xs text-gray-600 font-medium">
                          Action: {alert.action}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                  <p className="text-gray-600">No critical alerts at this time</p>
                  <p className="text-sm text-gray-500">All operators within normal parameters</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Balance Projections */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-6">
            <LineChart className="h-6 w-6 text-purple-600 mr-3" />
            <h3 className="text-xl font-bold text-gray-900">Balance Projections</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold">Next Month</h4>
                <TrendingUp className="h-6 w-6" />
              </div>
              <div className="text-2xl font-bold">{formatKip(balanceData.projections?.nextMonth || 0)}</div>
              <div className="text-blue-100 text-sm mt-2">
                Expected total balance
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold">Next Quarter</h4>
                <TrendingUp className="h-6 w-6" />
              </div>
              <div className="text-2xl font-bold">{formatKip(balanceData.projections?.nextQuarter || 0)}</div>
              <div className="text-purple-100 text-sm mt-2">
                Projected growth
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-semibold">Growth Rate</h4>
                {(balanceData.projections?.growth || 0) > 0 ?
                  <ArrowUpCircle className="h-6 w-6" /> :
                  <ArrowDownCircle className="h-6 w-6" />
                }
              </div>
              <div className="text-2xl font-bold">
                {(balanceData.projections?.growth || 0) > 0 ? '+' : ''}{balanceData.projections?.growth?.toFixed(1) || 0}%
              </div>
              <div className="text-green-100 text-sm mt-2">
                Quarterly growth trend
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default RegulatoryBalanceDashboard;